import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Upload, Trash2, Film, Image as ImageIcon, Loader2, Save, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ConfirmDialog";

/**
 * Appearance manager. Master-admin only surface for uploading background
 * media (image or video) and assigning it to specific pages with an
 * opacity value. Reads and writes through the appearance API.
 *
 * Rules applied:
 *   - No emojis. Only Lucide icons.
 *   - No em/en dashes in any UI copy.
 *   - Uses tokenized surfaces (Card + Shadcn primitives).
 *   - Every interactive element carries a stable data-testid.
 */

// The page keys the AppShell derives from pathname. Keep these in sync
// with the switch in `AppShell.jsx`.
const PAGE_KEYS = [
  { key: "dashboard",  label: "Dashboard" },
  { key: "horses",     label: "Horses" },
  { key: "rankings",   label: "Rankings" },
  { key: "breeding",   label: "Breeding" },
  { key: "staff",      label: "Staff" },
  { key: "inventory",  label: "Inventory (Feed and Tack)" },
  { key: "vehicles",   label: "Vehicles" },
  { key: "invoices",   label: "Invoices" },
  { key: "whatsapp",   label: "WhatsApp" },
  { key: "audit",      label: "Audit" },
  { key: "settings",   label: "Settings" },
  { key: "members",    label: "Members and Roles" },
  { key: "global",     label: "Fallback (Login, other)" },
];

const OPACITY_MIN = 0.05;
const OPACITY_MAX = 0.60;

function humanBytes(n) {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function normalisePages(rawPages) {
  // Ensure every known page key exists in the working state so the UI
  // can render a full matrix. Unknown keys returned by the server are
  // preserved as-is at the tail.
  const out = {};
  const known = new Set(PAGE_KEYS.map((p) => p.key));
  for (const p of PAGE_KEYS) {
    const existing = rawPages?.[p.key];
    out[p.key] = {
      media_id: existing?.media_id || null,
      opacity:  typeof existing?.opacity === "number" ? existing.opacity : 0.22,
      enabled:  existing?.enabled !== false, // default on
    };
  }
  for (const [k, v] of Object.entries(rawPages || {})) {
    if (!known.has(k)) out[k] = v;
  }
  return out;
}

export function AppearanceManager() {
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [uploading, setUploading] = useState(false);
  const [media, setMedia]       = useState([]);
  const [defaultMediaId, setDefaultMediaId] = useState(null);
  const [defaultOpacity, setDefaultOpacity] = useState(0.22);
  const [pages, setPages]       = useState(() => normalisePages({}));

  const mediaById = useMemo(() => Object.fromEntries(media.map((m) => [m.id, m])), [media]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/appearance/config");
      const d = res.data || {};
      setMedia(d.media || []);
      setDefaultMediaId(d.default_media_id || null);
      setDefaultOpacity(typeof d.default_opacity === "number" ? d.default_opacity : 0.22);
      setPages(normalisePages(d.pages || {}));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        toast.error("You do not have permission to manage appearance");
      } else {
        toast.error("Failed to load appearance config");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-selecting same file still triggers change
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File too large. Max 50 MB.");
      return;
    }
    if (!file.type.startsWith("video/") && !file.type.startsWith("image/")) {
      toast.error("Only image or video files are supported");
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/appearance/media", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Uploaded");
      // Prepend so the newest sits on top of the library.
      setMedia((prev) => [res.data.media, ...prev]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (mediaId) => {
    try {
      await api.delete(`/appearance/media/${mediaId}`);
      setMedia((prev) => prev.filter((m) => m.id !== mediaId));
      if (defaultMediaId === mediaId) setDefaultMediaId(null);
      setPages((prev) => {
        const next = { ...prev };
        for (const [k, v] of Object.entries(next)) {
          if (v?.media_id === mediaId) next[k] = { ...v, media_id: null };
        }
        return next;
      });
      toast.success("Deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
      throw err;
    }
  };

  const setPage = (key, patch) => {
    setPages((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        default_media_id: defaultMediaId,
        default_opacity: defaultOpacity,
        pages: pages,
      };
      await api.put("/appearance/config", payload);
      toast.success("Appearance saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="rounded-xl" data-testid="appearance-manager-card">
      <CardHeader>
        <div className="flex flex-row items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">Appearance</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Upload background videos or images and assign them to specific pages.
              Only master admins can manage this.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={load}
              disabled={loading}
              data-testid="appearance-reload-button"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              onClick={save}
              disabled={saving || loading}
              data-testid="appearance-save-button"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Save</>}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-8">
        {/* Upload zone */}
        <section data-testid="appearance-upload-section">
          <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Upload media</h4>
          <label
            className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border p-6 cursor-pointer hover:bg-[hsl(var(--surface-glass)/0.5)] transition-colors"
            data-testid="appearance-upload-dropzone"
          >
            <Upload className="h-6 w-6 text-muted-foreground" />
            <div className="text-sm">
              {uploading ? "Uploading..." : "Choose an image or video (max 50 MB)"}
            </div>
            <div className="text-xs text-muted-foreground">
              MP4 or WebM for video. JPG, PNG, or WebP for images.
            </div>
            <input
              type="file"
              accept="image/*,video/*"
              className="hidden"
              onChange={handleUpload}
              disabled={uploading}
              data-testid="appearance-upload-input"
            />
          </label>
        </section>

        {/* Media library */}
        <section data-testid="appearance-media-library">
          <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
            Media library ({media.length})
          </h4>
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : media.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No media uploaded yet. Add a file above to get started.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {media.map((m) => (
                <div
                  key={m.id}
                  className="rounded-md border border-border overflow-hidden bg-[hsl(var(--surface-glass)/0.5)]"
                  data-testid={`appearance-media-${m.id}`}
                >
                  <div className="relative aspect-video bg-black">
                    {m.kind === "video" ? (
                      <video
                        src={m.url}
                        muted
                        loop
                        playsInline
                        autoPlay
                        preload="metadata"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img src={m.url} alt="" className="w-full h-full object-cover" />
                    )}
                    <div className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-black/60 text-white px-2 py-0.5 text-[10px] uppercase tracking-wider">
                      {m.kind === "video" ? <Film className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                      {m.kind}
                    </div>
                  </div>
                  <div className="p-3 space-y-1">
                    <div className="text-xs font-medium truncate" title={m.filename}>{m.filename}</div>
                    <div className="text-[11px] text-muted-foreground flex items-center justify-between">
                      <span>{humanBytes(m.size_bytes)}</span>
                      <ConfirmDialog
                        title={`Delete "${m.filename}"?`}
                        description={
                          <>
                            <p>This media will be permanently removed from the library. Any page that currently uses <span className="font-medium text-foreground">{m.filename}</span> as its background will fall back to the watermark placeholder immediately.</p>
                          </>
                        }
                        confirmLabel="Yes, delete media"
                        onConfirm={() => handleDelete(m.id)}
                        testId={`appearance-media-delete-${m.id}`}
                      >
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          data-testid={`appearance-media-delete-${m.id}`}
                          aria-label="Delete media"
                        >
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </ConfirmDialog>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Global default */}
        <section data-testid="appearance-default-section">
          <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Global default</h4>
          <p className="text-xs text-muted-foreground mb-3">
            Used on any page that has no explicit assignment below.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs">Media</Label>
              <Select
                value={defaultMediaId ?? "__none__"}
                onValueChange={(v) => setDefaultMediaId(v === "__none__" ? null : v)}
              >
                <SelectTrigger data-testid="appearance-default-media-select">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None (watermark)</SelectItem>
                  {media.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.kind === "video" ? "Video " : "Image "}
                      {m.filename}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs flex items-center justify-between">
                <span>Opacity</span>
                <span className="font-mono text-[11px]">{Math.round(defaultOpacity * 100)}%</span>
              </Label>
              <Slider
                min={Math.round(OPACITY_MIN * 100)}
                max={Math.round(OPACITY_MAX * 100)}
                step={1}
                value={[Math.round(defaultOpacity * 100)]}
                onValueChange={(v) => setDefaultOpacity((v?.[0] ?? 22) / 100)}
                data-testid="appearance-default-opacity"
              />
            </div>
          </div>
        </section>

        {/* Per-page assignments */}
        <section data-testid="appearance-assignments-section">
          <h4 className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Per page</h4>
          <p className="text-xs text-muted-foreground mb-3">
            Disable the toggle to suppress background media on that page entirely.
            Leave media blank to inherit the global default.
          </p>
          <div className="space-y-3">
            {PAGE_KEYS.map(({ key, label }) => {
              const cfg = pages[key] || { media_id: null, opacity: 0.22, enabled: true };
              const resolvedId = cfg.media_id ?? defaultMediaId;
              const resolvedMedia = resolvedId ? mediaById[resolvedId] : null;
              return (
                <div
                  key={key}
                  className="grid md:grid-cols-[160px_1fr_1fr_auto] items-center gap-3 rounded-md border border-border p-3"
                  data-testid={`appearance-page-row-${key}`}
                >
                  <div>
                    <div className="text-sm font-medium">{label}</div>
                    <div className="text-[11px] text-muted-foreground font-mono">{key}</div>
                  </div>
                  <div>
                    <Select
                      value={cfg.media_id ?? "__inherit__"}
                      onValueChange={(v) => setPage(key, { media_id: v === "__inherit__" ? null : v })}
                    >
                      <SelectTrigger data-testid={`appearance-page-media-${key}`}>
                        <SelectValue placeholder="Inherit global default" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__inherit__">Inherit global default</SelectItem>
                        {media.map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.kind === "video" ? "Video " : "Image "}
                            {m.filename}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {resolvedMedia && (
                      <div className="text-[11px] text-muted-foreground mt-1">
                        Resolves to {resolvedMedia.kind}: {resolvedMedia.filename}
                      </div>
                    )}
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-muted-foreground">Opacity</span>
                      <span className="font-mono">{Math.round((cfg.opacity ?? 0.22) * 100)}%</span>
                    </div>
                    <Slider
                      min={Math.round(OPACITY_MIN * 100)}
                      max={Math.round(OPACITY_MAX * 100)}
                      step={1}
                      value={[Math.round((cfg.opacity ?? 0.22) * 100)]}
                      onValueChange={(v) => setPage(key, { opacity: (v?.[0] ?? 22) / 100 })}
                      disabled={!cfg.enabled}
                      data-testid={`appearance-page-opacity-${key}`}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={cfg.enabled}
                      onCheckedChange={(v) => setPage(key, { enabled: !!v })}
                      data-testid={`appearance-page-enabled-${key}`}
                      aria-label={`Enable background on ${label}`}
                    />
                    <span className="text-[11px] text-muted-foreground">
                      {cfg.enabled ? "On" : "Off"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </CardContent>
    </Card>
  );
}

export default AppearanceManager;
